import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronLeft, Home, RefreshCw, CalendarDays, MessageSquare, Menu, Plus,
} from 'lucide-react';
import { stopImpersonation, isImpersonating } from '@/lib/impersonation';
import mapi from './api';

/** App-Flächen. Die Verwaltung bleibt auf ihrer eigenen Palette. */
export const C = {
  bg: '#F6F7F9',
  card: '#FFFFFF',
  cardHover: '#F8E8EA',
  border: '#DDE2E8',
  header: '#34445A',
  red: '#A92F3B',
  danger: '#922832',
  rose: '#F8E8EA',
  text: '#172033',
  dim: '#596579',
  accent: '#A92F3B',
  success: '#3D6B4F',
  successBg: '#E8F3EC',
};

/**
 * Blätter hängen am Dokument, nicht am scrollenden Inhalt.
 * Sonst erzeugt der Scrollbereich eine eigene Ebene und die Tab-Leiste liegt darüber.
 */
export function MobileSheet({ children, bare = false, scrim, className, style, onClick, ...rest }) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      {...rest}
      className={className || 'fixed inset-0 z-50 flex items-end justify-center'}
      style={{ background: scrim || 'rgba(0,0,0,0.45)', ...style }}
      onClick={onClick}
    >
      {bare ? children : (
        <div
          className="w-full max-w-md"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          onClick={(event) => event.stopPropagation()}
        >
          {children}
        </div>
      )}
    </div>,
    document.body,
  );
}

export function MobileShell({ children, bottomPad, bottomNav, onAction }) {
  // Jede App-Seite behält die Leiste. Nur Login übergibt bottomNav={null}.
  // Das Plus erscheint nur, wenn die Seite eine eigene Aktion übergibt.
  const nav = bottomNav === undefined ? <AppBottomNav onAction={onAction} /> : bottomNav;
  const pad = bottomPad ?? !!nav;
  const impersonating = isImpersonating();
  const { pathname } = useLocation();
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('mobile-shell-lock');
    return () => root.classList.remove('mobile-shell-lock');
  }, []);
  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const cap = window.Capacitor;
        if (!cap?.isNativePlatform?.() || cap.getPlatform?.() !== 'ios') return;
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        if (gone) return;
        const login = pathname.startsWith('/m/login');
        await StatusBar.setStyle({ style: login ? Style.Dark : Style.Light });
      } catch (_e) { /* Plugin optional */ }
    })();
    return () => { gone = true; };
  }, [pathname]);
  return (
    <div
      style={{ background: C.bg, color: C.text }}
      className="mobile-app flex min-h-0 w-full max-w-[100vw] flex-1 flex-col overflow-hidden"
      data-testid="mobile-app"
    >
      <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col overflow-hidden"
           style={{ background: C.bg }}>
        {impersonating && (
          <div className="flex shrink-0 items-center gap-2 px-3 py-2 text-xs"
               style={{ background: C.text, color: '#fff' }} data-testid="mobile-impersonation-banner">
            <span className="flex-1">Kontowechsel aktiv (Super-Admin)</span>
            <button onClick={() => stopImpersonation()} data-testid="mobile-stop-impersonation-btn"
                    className="px-2 py-1 rounded font-semibold" style={{ background: '#fff', color: C.text }}>
              Zurück
            </button>
          </div>
        )}
        <div
          className={`flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain [&>*]:shrink-0${pad ? ' mobile-shell-pad' : ''}`}
          data-testid="mobile-shell-content"
        >
          {children}
        </div>
        {nav}
      </div>
    </div>
  );
}

export function MobileHeader({ title, subtitle, onRefresh, backTo, right, leading }) {
  const nav = useNavigate();
  const ink = '#FFFFFF';
  return (
    <div className="mobile-below-clock-bar sticky top-0 z-20 flex items-center gap-2 px-3 pb-2.5"
         data-testid="mobile-page-header"
         style={{ background: C.header, color: ink }}>
      {backTo !== undefined && (
        <button onClick={() => nav(backTo || -1)} data-testid="mobile-back-btn"
                className="p-1.5 rounded-full" style={{ color: ink }}>
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}
      <button onClick={() => nav('/m')} data-testid="mobile-home-btn"
              className="p-1.5 rounded-full" style={{ color: ink }}>
        <Home className="w-5 h-5" />
      </button>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {leading}
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold truncate" data-testid="mobile-page-title" style={{ color: ink }}>{title}</h1>
          {subtitle}
        </div>
      </div>
      {right}
      {onRefresh && (
        <button onClick={onRefresh} data-testid="mobile-refresh-btn"
                className="p-1.5 rounded-full" style={{ color: ink }}>
          <RefreshCw className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}

export function CountBadge({ n, testId }) {
  if (!n) return null;
  return (
    <span
      className="min-w-[1.15rem] h-5 px-1.5 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
      style={{ background: C.red }}
      data-testid={testId}
    >
      {n > 99 ? '99+' : n}
    </span>
  );
}

export function FilterChips({ items, value, onChange, testIdPrefix }) {
  return (
    <div className="flex gap-2 overflow-x-auto px-4 pb-2">
      {items.map(({ k, label }) => {
        const on = value === k;
        return (
          <button
            key={k}
            type="button"
            data-testid={testIdPrefix ? `${testIdPrefix}-${k}` : undefined}
            className="shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold"
            style={{
              background: on ? C.red : C.card,
              color: on ? '#fff' : C.text,
              border: `1px solid ${on ? C.red : C.border}`,
            }}
            onClick={() => onChange(k)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function ListItem({ icon: Icon, label, sub, onClick, testId, right }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors active:opacity-70"
      style={{ background: C.card, borderBottom: `1px solid ${C.border}`, color: C.text }}
    >
      {Icon && <Icon className="w-5 h-5 shrink-0" style={{ color: C.red }} />}
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium truncate">{label}</span>
        {sub && <span className="block text-xs truncate" style={{ color: C.dim }}>{sub}</span>}
      </span>
      {right}
    </button>
  );
}

/** Untere Tab-Leiste: Start · Unterricht · Aktion(+) · Nachrichten · Menü */
export function MobileBottomNav({ unread = 0, onAction }) {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const at = (p) => pathname === p || (p !== '/m' && pathname.startsWith(p));

  const tab = (active) => ({ color: active ? C.red : C.dim });

  return (
    <nav
      className="z-30 w-full shrink-0"
      data-testid="mobile-bottom-nav"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)', background: C.card }}
    >
      <div className="max-w-md mx-auto flex items-end justify-around px-2 pt-2 pb-2"
           style={{ background: C.card, borderTop: `1px solid ${C.border}` }}>
        <button type="button" data-testid="mobile-nav-start" onClick={() => nav('/m')}
                className="flex flex-col items-center gap-0.5 min-w-[3.25rem] py-1">
          <Home className="w-5 h-5" style={tab(at('/m') && pathname === '/m')} />
          <span className="text-[10px] font-medium" style={tab(pathname === '/m')}>Start</span>
        </button>
        <button type="button" data-testid="mobile-nav-unterricht" onClick={() => nav('/m/unterricht')}
                className="flex flex-col items-center gap-0.5 min-w-[3.25rem] py-1">
          <CalendarDays className="w-5 h-5" style={tab(at('/m/unterricht') || at('/m/attendance'))} />
          <span className="text-[10px] font-medium" style={tab(at('/m/unterricht') || at('/m/attendance'))}>Unterricht</span>
        </button>
        {onAction ? (
          <button type="button" data-testid="mobile-nav-action" onClick={onAction}
                  className="relative -mt-5 flex flex-col items-center min-w-[3.5rem]">
            <span className="w-14 h-14 rounded-full flex items-center justify-center shadow-md"
                  style={{ background: C.red }}>
              <Plus className="w-7 h-7 text-white" strokeWidth={2.5} />
            </span>
            <span className="text-[10px] font-medium mt-0.5" style={{ color: C.dim }}>Aktion</span>
          </button>
        ) : (
          <span className="min-w-[3.5rem]" aria-hidden="true" />
        )}
        <button type="button" data-testid="mobile-nav-messages" onClick={() => nav('/m/messages')}
                className="relative flex flex-col items-center gap-0.5 min-w-[3.25rem] py-1">
          <MessageSquare className="w-5 h-5" style={tab(at('/m/messages') || at('/m/chat'))} />
          {unread > 0 && (
            <span className="absolute -top-0.5 right-1.5 min-w-[1rem] h-4 px-1 rounded-full text-[9px] font-bold text-white flex items-center justify-center"
                  style={{ background: C.red }} data-testid="mobile-nav-messages-badge">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
          <span className="text-[10px] font-medium" style={tab(at('/m/messages') || at('/m/chat'))}>Nachrichten</span>
        </button>
        <button type="button" data-testid="mobile-nav-menu" onClick={() => nav('/m/settings')}
                className="flex flex-col items-center gap-0.5 min-w-[3.25rem] py-1">
          <Menu className="w-5 h-5" style={tab(at('/m/settings'))} />
          <span className="text-[10px] font-medium" style={tab(at('/m/settings'))}>Menü</span>
        </button>
      </div>
    </nav>
  );
}

/** Untere Navigation auf den Hauptseiten, inklusive Nachrichten-Badge aus MAMV. */
export function AppBottomNav({ onAction }) {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const request = mapi.get('/messages/unread-count');
    if (request && typeof request.then === 'function') {
      request
        .then((r) => { if (!cancelled) setUnread(r.data?.unread || 0); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, []);
  return <MobileBottomNav unread={unread} onAction={onAction} />;
}
